import React, { useContext, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import { ToastContext } from "../../../../App";
import {
  useLazyGetUserListQuery,
  useUpdateUserMutation,
} from "../../../../redux/services/user";
import { useGetOrganizationListQuery } from "../../../../redux/services/organization";

import * as styles from "../UserManagement.module.css";

const LIST_PATH = "/app/admin/users";

function UserEditPage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const orgSubdomain = searchParams.get("org");

  const { data: organizations } = useGetOrganizationListQuery();
  const organization = useMemo(
    () => organizations?.find((o) => o.subdomain === orgSubdomain),
    [organizations, orgSubdomain],
  );

  const [fetchUsers, { data: users }] = useLazyGetUserListQuery();

  useEffect(() => {
    if (orgSubdomain) fetchUsers(orgSubdomain);
  }, [orgSubdomain, fetchUsers]);

  const user = useMemo(
    () => users?.find((u) => String(u.id) === String(id)),
    [users, id],
  );

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { name: "", email: "", phone: "", role: "" },
  });

  useEffect(() => {
    if (user) {
      reset({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone?.slice(2) || "",
        role: user.role || "",
      });
    }
  }, [user, reset]);

  const [
    updateUser,
    {
      isSuccess: isUpdateUserSuccess,
      isError: isUpdateUserError,
      error: updateUserError,
    },
  ] = useUpdateUserMutation();

  useEffect(() => {
    if (isUpdateUserSuccess) {
      showToast("success", "User", "User updated!");
      navigate(LIST_PATH);
    }
  }, [isUpdateUserSuccess]);

  useEffect(() => {
    if (isUpdateUserError) {
      showToast(
        "error",
        updateUserError?.status,
        updateUserError?.data?.message ||
          (updateUserError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateUserError]);

  const onSaveEdit = (data) => {
    if (!organization) {
      showToast("warn", "Warning", "Organization is missing");
      return;
    }
    updateUser({
      id,
      subdomain: organization.subdomain,
      payload: {
        name: data.name,
        phone: `+1${data.phone}`,
        email: data.email,
        userRole: data.role,
      },
    });
  };

  const title = user?.name ? `Edit User — ${user.name}` : "Edit User";

  return (
    <EntityFormPage
      title={title}
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onSaveEdit)}
        />
      }
    >
      <form onSubmit={handleSubmit(onSaveEdit)}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Name</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                placeholder="Name"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <div className={`field ${styles.dropDownWrapper}`}>
              <label>Role</label>
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Select role"
                className="w-full mb-3"
                panelClassName={styles.dropDownPanel}
                options={[
                  { label: "Admin", value: "admin" },
                  { label: "Manager", value: "manager" },
                  { label: "Chef", value: "chef" },
                ]}
              />
            </div>
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Email</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                placeholder="Email"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="phone"
          control={control}
          rules={{
            required: "Enter your phone number",
            minLength: { value: 10, message: "Phone number is invalid" },
          }}
          render={({ field, fieldState }) => (
            <div className="field">
              <label>Phone Number</label>
              <div>
                <div className="p-inputgroup flex-1">
                  <span
                    className={`p-inputgroup-addon mb-3 ${styles.phonePrefix}`}
                  >
                    +1
                  </span>
                  <InputMask
                    className={`w-full mb-3 ${styles.input} ${styles.inputPhone} ${
                      fieldState.invalid ? "p-invalid" : ""
                    }`}
                    placeholder="(999) 999-9999"
                    prefix="+1"
                    mask="(999) 999-9999"
                    unmask={true}
                    {...field}
                  />
                </div>
                {fieldState.error && (
                  <small className="p-error">{fieldState.error.message}</small>
                )}
              </div>
            </div>
          )}
        />

        <button type="submit" hidden />
      </form>
    </EntityFormPage>
  );
}

export default UserEditPage;
